import React, {
  memo,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useIsInViewport from 'use-is-in-viewport';
import { AppContext } from '@prisjakt/ui-app-context';
import Clickable from '@prisjakt/ui-clickable';
import { IconEdit, IconInfoFilled } from '@prisjakt/ui/icon';
import T from '@prisjakt/ui-translate';
import UserAvatar from '@prisjakt/ui-user-avatar';
import ReviewPrompt from '../../areas/reviews/components/prompt';
import ReviewText from './components/review-text';
import Product from './components/product';
import { StoreCard } from './components/store';
import HelpfulButton from './components/helpful-button';
import Rating from './components/rating';
import ReportReviewModal from '../../areas/reviews/components/report';
import * as S from './styles';
import { likeReviewToggle } from '../../areas/reviews/actions';
import {
  productReviewViewed,
  storeReviewViewed,
} from '../../areas/reviews/tracking';
import { Translation, BelongsTo } from './types';
import { Store } from '../../areas/reviews/types';
import { UserReview, User } from '../../types';
import { AppState } from '../../reducers';
import { useIsAuthenticated } from '../../hooks/use-is-authenticated';
import { SHOP_LOGO_WIDTH } from '../../areas/store/constants';
import { TooltipPlacement } from '../tooltip-new';
import {
  ButtonIcon,
  ButtonText,
  TextButton,
} from '@prisjakt/ui/button/variants';

const ICON_SIZE = 20;

type Props = {
  className?: string;
  id: UserReview['id'];
  type: 'store' | 'product';
  text: UserReview['reviewText'];
  rating: UserReview['rating'];
  purchaseDate?: UserReview['purchaseDate'];
  translation?: Translation | null;
  reply?: UserReview['reply'];
  replyDate?: UserReview['replyDate'];
  createdAt: UserReview['createdAt'];
  updatedAt?: UserReview['updatedAt'];
  userId?: User['id'] | null;
  userAvatar?: User['avatar'];
  userDisplayName?: User['displayName'];
  userReviewCount?: User['reviewCount'];
  likes?: UserReview['likeScore'];
  views?: null;
  belongsTo: BelongsTo;
  product?: UserReview['product'] | null;
  store?: Store | null;
  isMyReview?: boolean;
  hideFooter?: boolean;
  hideUser?: boolean;
  hideProduct?: boolean;
  hideStore?: boolean;
  viaShop?: boolean | null;
};

const selectLikedReviews = (state: AppState) =>
  state.areas.account.me?.likes?.reviews;

const UserReviewCard = ({
  className,
  id,
  type,
  text,
  rating,
  purchaseDate,
  translation,
  reply,
  replyDate,
  createdAt,
  updatedAt,
  userId,
  userAvatar,
  userDisplayName,
  userReviewCount,
  likes,
  views,
  belongsTo,
  product,
  store,
  isMyReview,
  hideFooter = false,
  hideUser = false,
  hideStore = false,
  hideProduct = false,
  viaShop,
}: Props) => {
  const userReviewRef = useRef(null);
  const [isInViewport, wrappedTargetRef] = useIsInViewport({
    target: userReviewRef,
  });
  const { t, market, renderMode } = useContext(AppContext);
  const [reviewViewed, setReviewViewed] = useState(false);
  const [reportedReview, setReportedReview] = useState<string | null>(null);

  const dispatch = useDispatch();
  const likedReviews = useSelector(selectLikedReviews);
  const isAuthenticated = useIsAuthenticated();

  const liked = Boolean(likedReviews?.includes(id));
  const isMine = isMyReview && isAuthenticated;
  const nativeMode = renderMode === 'native';

  useEffect(() => {
    // Track viewed reviews
    if (isInViewport && !reviewViewed) {
      setReviewViewed(true);
      if (belongsTo.type === 'product') {
        productReviewViewed();
      } else if (belongsTo.type === 'store') {
        storeReviewViewed();
      }
    }
  }, [isInViewport, belongsTo.type, reviewViewed]);

  const handleVote = useCallback(() => {
    if (isAuthenticated) {
      dispatch(likeReviewToggle(id, belongsTo.type));
    }
  }, [dispatch, isAuthenticated, id, belongsTo.type]);

  const handleReport = useCallback(() => {
    setReportedReview(id);
  }, [id]);

  const onReportCompleted = useCallback(() => {
    setReportedReview(null);
  }, []);

  const currentReview = useMemo(
    () => ({
      id,
      rating,
      reviewText: text,
      purchaseDate,
    }),
    [id, rating, text, purchaseDate]
  );

  const reviewReply = useMemo(
    () => ({
      name: belongsTo.name,
      text: reply,
      date: replyDate,
    }),
    [belongsTo.name, reply, replyDate]
  );

  // NOTE: Hides the purchase date on markets where this is not a requirement
  // because it does not accurately represent the actual date of purchase.
  // Should be fixed and enabled when the new review service is in place.
  const showPurchaseDate = Boolean(
    market.modules?.userReviewPurchaseDate?.enabled
  );

  if (!id) return null;

  return (
    <>
      {reportedReview ? (
        <ReportReviewModal
          review={reportedReview}
          onComplete={onReportCompleted}
          type={belongsTo.type}
        />
      ) : null}

      <S.UserReviewCard
        className={className}
        ref={wrappedTargetRef}
        data-test="UserReviewCard"
      >
        <S.User>
          {userDisplayName && userId ? (
            <S.UserInfo>
              <S.UserAvatar to={`/user/${userId}`} isExternal={nativeMode}>
                <UserAvatar
                  avatar={userAvatar}
                  displayName={userDisplayName}
                  size={S.USER_AVATAR_SIZE}
                />
              </S.UserAvatar>

              <S.UserDisplayName>
                <Clickable to={`/user/${userId}`} isExternal={nativeMode}>
                  {userDisplayName}
                </Clickable>
              </S.UserDisplayName>

              {userReviewCount && userReviewCount > 0 ? (
                <S.UserReviewCount>
                  <T domain="reviews" n={userReviewCount || 1}>
                    user.total-reviews.md
                  </T>
                </S.UserReviewCount>
              ) : null}
            </S.UserInfo>
          ) : (
            type === 'store' &&
            !hideUser && (
              <S.AnonymousUser>
                <S.AnonymousUserAvatar>
                  <img
                    alt={t('reviews', 'user.guest')}
                    src={`${market.cdn}/g/rfe/icons/anon-avatar.png
                    `}
                  />
                </S.AnonymousUserAvatar>
                <S.AnonymousUserDisplayName>
                  {t('reviews', 'user.guest')}
                </S.AnonymousUserDisplayName>
              </S.AnonymousUser>
            )
          )}
        </S.User>

        <S.Main>
          <S.Review>
            {product && !hideProduct && (
              <Product
                name={product.name}
                image={product.media?.first}
                rating={product.userReviewSummary.rating}
                ratingCount={product.userReviewSummary.count}
                price={product.priceSummary.regular}
                pathName={product.pathName}
              />
            )}

            {store && !hideStore && (
              <StoreCard
                id={store.id}
                name={store.name}
                hasLogo={store.hasLogo}
                featured={store.featured}
                pathname={store.pathName}
                rating={store.userReviewSummary.rating}
                reviewCount={store.userReviewSummary.countTotal}
                renderMode={renderMode}
                logoUrl={store.logo}
                logoWidth={SHOP_LOGO_WIDTH}
              />
            )}

            <S.Header>
              {typeof rating === 'number' ? (
                <Rating type={type} rating={rating} />
              ) : null}
              <S.Dates>
                {createdAt || updatedAt ? (
                  <>
                    <S.CreateDate>
                      <S.Divider />
                      {updatedAt &&
                        t('reviews', 'info.edited', {
                          date: updatedAt,
                        })}
                      {!updatedAt &&
                        createdAt &&
                        t('reviews', 'info.created', {
                          date: createdAt,
                        })}
                    </S.CreateDate>
                  </>
                ) : null}

                {showPurchaseDate && purchaseDate ? (
                  <>
                    {createdAt || updatedAt ? <S.Divider /> : null}
                    {t('reviews', 'info.purchased', {
                      date: purchaseDate,
                    })}
                  </>
                ) : null}
              </S.Dates>
              {viaShop && (
                <S.ViaShop>
                  <S.Divider />
                  <S.ViaShopTooltip
                    placement={TooltipPlacement.TOP}
                    title={t('reviews', 'store.via-shop-tooltip')}
                  >
                    <IconInfoFilled size={16} color="maybeBlue700" />
                    <S.ViaShopText>
                      {t('reviews', 'store.via-shop')}
                    </S.ViaShopText>
                  </S.ViaShopTooltip>
                </S.ViaShop>
              )}
            </S.Header>

            <ReviewText
              id={id}
              text={text}
              translation={translation}
              reply={reviewReply}
            />

            {!hideFooter ? (
              <S.Footer>
                {likes && isMine ? (
                  <S.Statistic>
                    <S.ThumbsUpIcon size={16} /> {likes}
                  </S.Statistic>
                ) : null}

                {views ? (
                  <S.Statistic>
                    <S.EyeIcon size={16} /> {views}
                  </S.Statistic>
                ) : null}

                {!isMine ? (
                  <HelpfulButton
                    onClick={handleVote}
                    liked={liked}
                    likes={likes}
                    size={ICON_SIZE}
                  />
                ) : null}

                {!isMine ? (
                  <S.ReportButton onClick={handleReport}>
                    <ButtonIcon>
                      <S.FlagIcon size={ICON_SIZE} />
                    </ButtonIcon>
                  </S.ReportButton>
                ) : null}

                {isMine ? (
                  <S.Edit>
                    <ReviewPrompt
                      belongsTo={belongsTo}
                      currentReview={currentReview}
                      store={store}
                      control={
                        <TextButton>
                          <ButtonIcon>
                            <IconEdit />
                          </ButtonIcon>
                          <ButtonText>{t('common', 'button.edit')}</ButtonText>
                        </TextButton>
                      }
                    />
                  </S.Edit>
                ) : null}
              </S.Footer>
            ) : null}
          </S.Review>
        </S.Main>
      </S.UserReviewCard>
    </>
  );
};

export default memo(UserReviewCard);
